'use client'

import { cn } from '@/utilities/ui'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import * as React from 'react'

/**
 * Select - A customizable dropdown selection component built on Radix UI
 * @description
 * A comprehensive select component that provides a dropdown interface for choosing from a list of options.
 * Built on Radix UI Select primitives with full keyboard navigation, accessibility features, and customizable styling.
 * Supports controlled and uncontrolled usage, grouping, labels, separators, and form integration.
 * 
 * @example
 * // Basic select with static options
 * <Select>
 *   <SelectTrigger className="w-48">
 *     <SelectValue placeholder="Choose an option..." />
 *   </SelectTrigger>
 *   <SelectContent>
 *     <SelectItem value="option1">Option 1</SelectItem>
 *     <SelectItem value="option2">Option 2</SelectItem>
 *     <SelectItem value="option3">Option 3</SelectItem>
 *   </SelectContent>
 * </Select>
 * 
 * @example
 * // Controlled select with state management
 * const [value, setValue] = React.useState("")
 * 
 * <Select value={value} onValueChange={setValue}>
 *   <SelectTrigger className="w-64">
 *     <SelectValue placeholder="Select a framework..." />
 *   </SelectTrigger>
 *   <SelectContent>
 *     <SelectItem value="react">React</SelectItem>
 *     <SelectItem value="vue">Vue</SelectItem>
 *     <SelectItem value="angular">Angular</SelectItem>
 *   </SelectContent>
 * </Select>
 * 
 * @example
 * // Select with groups and labels
 * <Select>
 *   <SelectTrigger className="w-56">
 *     <SelectValue placeholder="Choose technology..." />
 *   </SelectTrigger>
 *   <SelectContent>
 *     <SelectGroup>
 *       <SelectLabel>Frontend</SelectLabel>
 *       <SelectItem value="react">React</SelectItem>
 *       <SelectItem value="vue">Vue.js</SelectItem>
 *       <SelectItem value="angular">Angular</SelectItem>
 *     </SelectGroup>
 *     <SelectSeparator />
 *     <SelectGroup>
 *       <SelectLabel>Backend</SelectLabel>
 *       <SelectItem value="nodejs">Node.js</SelectItem>
 *       <SelectItem value="python">Python</SelectItem>
 *       <SelectItem value="java">Java</SelectItem>
 *     </SelectGroup>
 *   </SelectContent>
 * </Select>
 * 
 * @example
 * // Form integration with react-hook-form
 * import { useForm, Controller } from "react-hook-form"
 * 
 * const { control, handleSubmit } = useForm()
 * 
 * <form onSubmit={handleSubmit(onSubmit)}>
 *   <Controller
 *     name="category"
 *     control={control}
 *     render={({ field }) => (
 *       <Select value={field.value} onValueChange={field.onChange}>
 *         <SelectTrigger className="w-full">
 *           <SelectValue placeholder="Select category..." />
 *         </SelectTrigger>
 *         <SelectContent>
 *           <SelectItem value="electronics">Electronics</SelectItem>
 *           <SelectItem value="clothing">Clothing</SelectItem>
 *           <SelectItem value="books">Books</SelectItem>
 *         </SelectContent>
 *       </Select>
 *     )}
 *   />
 * </form>
 * 
 * @example
 * // Select with disabled options and custom styling
 * <Select>
 *   <SelectTrigger className="w-52 border-2 border-blue-300">
 *     <SelectValue placeholder="Select status..." />
 *   </SelectTrigger>
 *   <SelectContent>
 *     <SelectItem value="active">Active</SelectItem>
 *     <SelectItem value="pending">Pending</SelectItem>
 *     <SelectItem value="inactive" disabled>
 *       Inactive (unavailable)
 *     </SelectItem>
 *     <SelectItem value="archived">Archived</SelectItem>
 *   </SelectContent>
 * </Select>
 */

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

/**
 * SelectTrigger - The clickable trigger button that opens the select dropdown
 * @description
 * A styled button component that serves as the trigger for opening the select dropdown.
 * Displays the selected value or placeholder text and includes a chevron icon to indicate
 * the dropdown functionality. Supports all standard button accessibility features.
 * 
 * @param className - Additional CSS classes to apply to the trigger
 * @param children - Content to display inside the trigger (typically SelectValue)
 * @param ref - Ref to the underlying button element
 */
const SelectTrigger: React.FC<
  { ref?: React.Ref<HTMLButtonElement> } & React.ComponentProps<typeof SelectPrimitive.Trigger>
> = ({ children, className, ref, ...props }) => (
  <SelectPrimitive.Trigger
    className={cn(
      'flex h-10 w-full items-center justify-between rounded border border-input bg-background px-3 py-2 text-inherit ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
      className,
    )}
    ref={ref}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
)

/**
 * SelectScrollUpButton - Scroll button for navigating up in long option lists
 * @description
 * A button that appears at the top of the select dropdown when there are more options
 * above the current view. Allows users to scroll up through the options list.
 * Automatically shows/hides based on scroll position.
 * 
 * @param className - Additional CSS classes to apply to the scroll button
 * @param ref - Ref to the underlying div element
 */
const SelectScrollUpButton: React.FC<
  { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>
> = ({ className, ref, ...props }) => (
  <SelectPrimitive.ScrollUpButton
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    ref={ref}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
)

/**
 * SelectScrollDownButton - Scroll button for navigating down in long option lists
 * @description
 * A button that appears at the bottom of the select dropdown when there are more options
 * below the current view. Allows users to scroll down through the options list.
 * Automatically shows/hides based on scroll position.
 * 
 * @param className - Additional CSS classes to apply to the scroll button
 * @param ref - Ref to the underlying div element
 */
const SelectScrollDownButton: React.FC<
  { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<
    typeof SelectPrimitive.ScrollDownButton
  >
> = ({ className, ref, ...props }) => (
  <SelectPrimitive.ScrollDownButton
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    ref={ref}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
)

/**
 * SelectContent - The dropdown content container that holds the select options
 * @description
 * A portal-rendered dropdown container that appears when the select is opened.
 * Contains the list of selectable options with smooth animations, scroll controls,
 * and proper positioning relative to the trigger. Supports both popper and item-aligned positioning.
 * 
 * @param children - The select options and groups to display
 * @param className - Additional CSS classes to apply to the content container
 * @param position - Positioning strategy: 'popper' (default) or 'item-aligned'
 * @param ref - Ref to the underlying div element
 */
const SelectContent: React.FC<
  {
    ref?: React.Ref<HTMLDivElement>
  } & React.ComponentProps<typeof SelectPrimitive.Content>
> = ({ children, className, position = 'popper', ref, ...props }) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      className={cn(
        'relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded border bg-card text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      ref={ref}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
)

/**
 * SelectLabel - A label component for grouping related select options
 * @description
 * A styled label that can be used within SelectGroup to provide a heading
 * for a group of related options. Helps organize options into logical categories
 * and improves accessibility by providing context for screen readers.
 * 
 * @param className - Additional CSS classes to apply to the label
 * @param ref - Ref to the underlying div element
 * @param children - The label text to display
 */
const SelectLabel: React.FC<
  { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<typeof SelectPrimitive.Label>
> = ({ className, ref, ...props }) => (
  <SelectPrimitive.Label
    className={cn('py-1.5 pl-8 pr-2 text-sm font-semibold', className)}
    ref={ref}
    {...props}
  />
)

/**
 * SelectItem - An individual selectable option within the select dropdown
 * @description
 * A clickable option component that represents a single choice in the select dropdown.
 * Includes a check icon indicator when selected, supports keyboard navigation,
 * and can be disabled. The value prop is required and represents the data value
 * that will be returned when this option is selected.
 * 
 * @param value - The data value for this option (required)
 * @param children - The display text/content for this option
 * @param className - Additional CSS classes to apply to the item
 * @param disabled - Whether this option is disabled and cannot be selected
 * @param ref - Ref to the underlying div element
 */
const SelectItem: React.FC<
  { ref?: React.Ref<HTMLDivElement>; value: string } & React.ComponentProps<
    typeof SelectPrimitive.Item
  >
> = ({ children, className, ref, ...props }) => (
  <SelectPrimitive.Item
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    ref={ref}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
)

/**
 * SelectSeparator - A visual separator for dividing groups of select options
 * @description
 * A horizontal line component that provides visual separation between groups
 * of options in the select dropdown. Useful for creating logical divisions
 * between different categories of options to improve readability and organization.
 * 
 * @param className - Additional CSS classes to apply to the separator
 * @param ref - Ref to the underlying div element
 */
const SelectSeparator: React.FC<
  { ref?: React.Ref<HTMLDivElement> } & React.ComponentProps<typeof SelectPrimitive.Separator>
> = ({ className, ref, ...props }) => (
  <SelectPrimitive.Separator
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    ref={ref}
    {...props}
  />
)

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
